import { useCallback, useEffect, useMemo, useState } from "react";
import { clsx } from "clsx";
import { ChevronDown, Earth, Search } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { Input } from "@/design-system/components/Input/Input";
import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import {
  COUNTRY_REGION_OPTIONS,
  isBulkSelectable,
  QUICK_SELECT_GROUPS,
  REGION_OPTION_LOOKUP,
  QuickSelectGroup,
  RegionOption,
} from "./constants";
import { Button } from "@/design-system/components/Button/Button";

const DETECTED_COUNTRY_REGION_OPTIONS = COUNTRY_REGION_OPTIONS.map(
  (country) => ({
    ...country,
    regions: country.regions.filter((region) => region.source !== "undetected"),
  }),
);

interface RegionMultiSelectProps {
  onSelectionChange: (selected: RegionOption[]) => void;
  /** Makes the selection controlled; `defaultValues` is ignored when set. */
  values?: string[];
  defaultValues?: string[];
  disabledValues?: string[];
  disabled?: boolean;
  /** Defaults to open when anything starts selected. */
  defaultOpen?: boolean;
  hideUndetectedRegions?: boolean;
}
export function RegionMultiSelect({
  onSelectionChange,
  values,
  defaultValues,
  disabledValues,
  disabled,
  defaultOpen,
  hideUndetectedRegions,
}: RegionMultiSelectProps) {
  const countries = hideUndetectedRegions
    ? DETECTED_COUNTRY_REGION_OPTIONS
    : COUNTRY_REGION_OPTIONS;
  const initialValues = values ?? defaultValues ?? [];
  const [uncontrolledValues, setUncontrolledValues] =
    useState<string[]>(initialValues);
  const selectedValues = values ?? uncontrolledValues;
  const [selectedCountryCode, setSelectedCountryCode] = useState<
    string | undefined
  >(() => {
    const firstSelected = initialValues.find((value) =>
      REGION_OPTION_LOOKUP.has(value),
    );
    if (firstSelected) {
      return REGION_OPTION_LOOKUP.get(firstSelected)?.countryCode;
    }
    return countries[0]?.code;
  });
  const [countryQuery, setCountryQuery] = useState("");
  const [regionQuery, setRegionQuery] = useState("");

  const disabledValuesSet = useMemo(
    () => new Set(disabledValues ?? []),
    [disabledValues],
  );
  const selectedValuesSet = useMemo(
    () => new Set(selectedValues),
    [selectedValues],
  );

  const selectedRegions = useMemo(
    () =>
      selectedValues
        .map((value) => REGION_OPTION_LOOKUP.get(value))
        .filter((region): region is RegionOption => Boolean(region)),
    [selectedValues],
  );

  const selectedCountByCountry = useMemo(() => {
    return selectedRegions.reduce((acc, region) => {
      acc.set(region.countryCode, (acc.get(region.countryCode) ?? 0) + 1);
      return acc;
    }, new Map<string, number>());
  }, [selectedRegions]);

  const filteredCountries = useMemo(() => {
    const query = countryQuery.trim().toLowerCase();
    if (!query) return countries;
    return countries.filter((country) =>
      country.name.toLowerCase().includes(query),
    );
  }, [countries, countryQuery]);

  useEffect(() => {
    if (filteredCountries.length === 0) {
      setSelectedCountryCode(undefined);
      return;
    }
    if (
      !selectedCountryCode ||
      !filteredCountries.some((country) => country.code === selectedCountryCode)
    ) {
      setSelectedCountryCode(filteredCountries[0].code);
    }
  }, [filteredCountries, selectedCountryCode]);

  const activeCountry = useMemo(() => {
    if (!selectedCountryCode) return undefined;
    return countries.find((country) => country.code === selectedCountryCode);
  }, [countries, selectedCountryCode]);

  const filteredRegions = useMemo(() => {
    if (!activeCountry) return [];
    const query = regionQuery.trim().toLowerCase();
    if (!query) return activeCountry.regions;
    return activeCountry.regions.filter(
      (region) =>
        region.regionName.toLowerCase().includes(query) ||
        region.value.toLowerCase().includes(query),
    );
  }, [activeCountry, regionQuery]);

  const quickSelectGroups = useMemo(() => {
    const countryRegionLookup = new Map(
      countries.map((country) => [country.code, country.regions]),
    );
    return QUICK_SELECT_GROUPS.map((group) => {
      const regions = group.countryCodes.flatMap(
        (countryCode) => countryRegionLookup.get(countryCode) ?? [],
      );
      const regionValues = regions.map((region) => region.value);
      const selectableValues = regions
        .filter(isBulkSelectable)
        .map((region) => region.value)
        .filter((value) => !disabledValuesSet.has(value));
      return { ...group, regionValues, selectableValues };
    }).filter((group) => group.regionValues.length > 0);
  }, [countries, disabledValuesSet]);

  const selectedValuesLookup = useMemo(
    () => new Set(selectedValues),
    [selectedValues],
  );

  const isQuickSelectActive = useCallback(
    (group: QuickSelectGroup & { selectableValues: string[] }) => {
      if (group.selectableValues.length === 0) return false;
      return group.selectableValues.every((value) =>
        selectedValuesLookup.has(value),
      );
    },
    [selectedValuesLookup],
  );

  const allFilteredCountrySelectableValues = useMemo(() => {
    return filteredCountries.flatMap((country) =>
      country.regions
        .filter(
          (region) =>
            isBulkSelectable(region) && !disabledValuesSet.has(region.value),
        )
        .map((region) => region.value),
    );
  }, [filteredCountries, disabledValuesSet]);

  const allFilteredCountryRegionValues = useMemo(() => {
    return filteredCountries.flatMap((country) =>
      country.regions.map((region) => region.value),
    );
  }, [filteredCountries]);

  const selectableRegionValues = useMemo(() => {
    if (!activeCountry) return [];
    return activeCountry.regions
      .filter((region) => !disabledValuesSet.has(region.value))
      .map((region) => region.value);
  }, [activeCountry, disabledValuesSet]);

  const bulkSelectableRegionValues = useMemo(() => {
    if (!activeCountry) return [];
    return activeCountry.regions
      .filter(
        (region) =>
          isBulkSelectable(region) && !disabledValuesSet.has(region.value),
      )
      .map((region) => region.value);
  }, [activeCountry, disabledValuesSet]);

  const applySelection = useCallback(
    (nextValues: string[]) => {
      setUncontrolledValues(nextValues);
      const nextSelected = nextValues.reduce<RegionOption[]>((acc, value) => {
        const region = REGION_OPTION_LOOKUP.get(value);
        if (region) acc.push(region);
        return acc;
      }, []);
      onSelectionChange?.(nextSelected);
    },
    [onSelectionChange],
  );

  // When disabledValues changes, prune any already-selected values that are
  // now disabled (e.g. domains changed creating new overlap with another config).
  useEffect(() => {
    if (disabledValuesSet.size === 0) return;
    const pruned = selectedValues.filter(
      (value) => !disabledValuesSet.has(value),
    );
    if (pruned.length !== selectedValues.length) {
      applySelection(pruned);
    }
  }, [disabledValuesSet]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally only reacting to disabledValues changes

  const handleClearAll = useCallback(() => {
    if (disabled) return;
    applySelection([]);
  }, [applySelection, disabled]);

  const handleQuickSelect = useCallback(
    (group: QuickSelectGroup & { selectableValues: string[] }) => {
      if (disabled || group.selectableValues.length === 0) return;
      if (isQuickSelectActive(group)) {
        const nextValues = selectedValues.filter(
          (value) => !group.selectableValues.includes(value),
        );
        applySelection(nextValues);
        return;
      }
      const nextValues = Array.from(
        new Set([...selectedValues, ...group.selectableValues]),
      );
      applySelection(nextValues);
    },
    [applySelection, disabled, isQuickSelectActive, selectedValues],
  );

  const handleSelectAllRegions = useCallback(() => {
    if (disabled || !activeCountry || bulkSelectableRegionValues.length === 0)
      return;
    const nextValues = Array.from(
      new Set([...selectedValues, ...bulkSelectableRegionValues]),
    );
    applySelection(nextValues);
  }, [
    activeCountry,
    applySelection,
    disabled,
    bulkSelectableRegionValues,
    selectedValues,
  ]);

  const handleUnselectAllRegions = useCallback(() => {
    if (disabled || !activeCountry) return;
    const nextValues = selectedValues.filter(
      (value) => !selectableRegionValues.includes(value),
    );
    applySelection(nextValues);
  }, [
    activeCountry,
    applySelection,
    disabled,
    selectableRegionValues,
    selectedValues,
  ]);

  const handleSelectAllCountries = useCallback(() => {
    if (disabled || allFilteredCountrySelectableValues.length === 0) return;
    const nextValues = Array.from(
      new Set([...selectedValues, ...allFilteredCountrySelectableValues]),
    );
    applySelection(nextValues);
  }, [
    allFilteredCountrySelectableValues,
    applySelection,
    disabled,
    selectedValues,
  ]);

  const handleClearAllCountries = useCallback(() => {
    if (disabled) return;
    const valuesToRemove = new Set(allFilteredCountryRegionValues);
    const nextValues = selectedValues.filter(
      (value) => !valuesToRemove.has(value),
    );
    applySelection(nextValues);
  }, [
    allFilteredCountryRegionValues,
    applySelection,
    disabled,
    selectedValues,
  ]);

  const handleToggleRegion = useCallback(
    (region: RegionOption) => {
      if (disabled || disabledValuesSet.has(region.value)) return;
      if (selectedValuesSet.has(region.value)) {
        applySelection(
          selectedValues.filter((value) => value !== region.value),
        );
        return;
      }
      applySelection([...selectedValues, region.value]);
    },
    [
      applySelection,
      disabled,
      disabledValuesSet,
      selectedValues,
      selectedValuesSet,
    ],
  );

  const [isOpen, setIsOpen] = useState(
    () => defaultOpen ?? initialValues.length > 0,
  );

  return (
    <div className="w-full space-y-2">
      <span className="text-textSecondary text-sm font-medium">
        Select regions
      </span>

      <div
        className={clsx(
          "border-borderSubtle bg-surface-default w-full rounded-xl border",
          disabled && "opacity-60",
        )}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <button
            type="button"
            onClick={() => setIsOpen((prev) => !prev)}
            className="flex flex-1 cursor-pointer items-center gap-2"
          >
            <ChevronDown
              className={clsx(
                "text-textSubtle flex-shrink-0 transition-transform duration-200",
                isOpen && "rotate-180",
              )}
              size={20}
            />
            <span className="text-textDefault text-sm font-medium">
              {selectedRegions.length > 0
                ? `${selectedRegions.length} region${selectedRegions.length === 1 ? "" : "s"} selected`
                : "No regions selected"}
            </span>
            {selectedRegions.length > 0 && (
              <Tooltip
                content={
                  <ul className="max-h-60 list-disc space-y-1 overflow-y-auto pl-4">
                    {selectedRegions.map((region) => (
                      <li key={region.value} className="leading-snug">
                        {region.label}
                      </li>
                    ))}
                  </ul>
                }
                contentProps={{ className: "max-w-80" }}
              >
                <span className="inline-flex min-w-[28px] items-center justify-center rounded-full bg-blue-500 px-2 py-0.5 text-xs font-semibold text-white">
                  {selectedRegions.length}
                </span>
              </Tooltip>
            )}
          </button>
          {selectedRegions.length > 0 && (
            <Button
              variant="link"
              className="h-auto flex-shrink-0 p-0 text-xs"
              type="button"
              onClick={handleClearAll}
              disabled={disabled}
            >
              Clear all
            </Button>
          )}
        </div>

        {isOpen && (
          <>
            <div className="border-borderSubtle flex flex-wrap items-center gap-3 border-t px-4 py-3">
              <div className="text-textDefault flex items-center gap-2 text-sm font-medium">
                <Earth className="text-textSubtle" size={20} />
                Quick select
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {quickSelectGroups.map((group) => {
                  const isActive = isQuickSelectActive(group);
                  const isDisabled =
                    disabled || group.selectableValues.length === 0;
                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => handleQuickSelect(group)}
                      disabled={isDisabled}
                      className={twMerge(
                        clsx(
                          "text-textDefault border-borderSubtle cursor-pointer rounded-full border px-3 py-1 text-sm font-medium transition",
                          isActive &&
                            "border-interactive-selected bg-surface-selected text-textActionPressed",
                          !isActive && "hover:bg-surface-hover",
                          isDisabled && "cursor-not-allowed opacity-50",
                        ),
                      )}
                    >
                      {group.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid w-full grid-cols-1 md:grid-cols-2">
              <div className="border-borderSubtle flex flex-col border-b md:border-r md:border-b-0">
                <div className="space-y-3 px-4 py-3">
                  <div className="text-textDefault text-sm font-medium">
                    Countries
                  </div>
                  <Input
                    value={countryQuery}
                    onChange={(event) => setCountryQuery(event.target.value)}
                    placeholder="Search countries..."
                    leadingIcon={
                      <Search className="text-iconDefault" size={16} />
                    }
                    disabled={disabled}
                    className="py-2"
                  />
                  <div className="flex items-center justify-end gap-3">
                    <Button
                      variant="link"
                      className="h-auto p-0 text-xs"
                      type="button"
                      onClick={handleSelectAllCountries}
                      disabled={
                        disabled ||
                        allFilteredCountrySelectableValues.length === 0
                      }
                    >
                      Select all
                    </Button>
                    <Button
                      variant="link"
                      className="h-auto p-0 text-xs"
                      type="button"
                      onClick={handleClearAllCountries}
                      disabled={disabled}
                    >
                      Clear all
                    </Button>
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {filteredCountries.length === 0 ? (
                    <div className="text-textSubtle px-4 py-3 text-sm">
                      No countries match your search.
                    </div>
                  ) : (
                    filteredCountries.map((country) => {
                      const isActive = country.code === selectedCountryCode;
                      const selectedCount =
                        selectedCountByCountry.get(country.code) ?? 0;
                      return (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => setSelectedCountryCode(country.code)}
                          disabled={disabled}
                          className={clsx(
                            "flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm transition",
                            isActive &&
                              "text-textDefault border-interactive-selected bg-surface-selected border-l-4",
                            !isActive && "hover:bg-surface-hover",
                            disabled && "cursor-not-allowed",
                          )}
                        >
                          <div className="space-y-1">
                            <div className="text-textDefault font-medium">
                              {country.name}
                            </div>
                            <div className="text-textSubtle text-xs">
                              {country.regions.length} regions
                            </div>
                          </div>
                          {selectedCount > 0 && (
                            <span className="inline-flex size-6 items-center justify-center rounded-full bg-blue-500 text-xs font-semibold text-white">
                              {selectedCount}
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                <div className="space-y-3 px-4 py-3">
                  <div className="text-textDefault text-sm font-medium">
                    {activeCountry
                      ? `${activeCountry.name} regions`
                      : "Regions"}
                  </div>
                  <Input
                    value={regionQuery}
                    onChange={(event) => setRegionQuery(event.target.value)}
                    placeholder="Search regions..."
                    leadingIcon={
                      <Search className="text-iconDefault" size={16} />
                    }
                    disabled={disabled || !activeCountry}
                    className="py-2"
                  />
                  {activeCountry && (
                    <div className="flex items-center justify-end gap-3">
                      <Button
                        variant="link"
                        className="h-auto p-0 text-xs"
                        type="button"
                        onClick={handleSelectAllRegions}
                        disabled={
                          disabled || bulkSelectableRegionValues.length === 0
                        }
                      >
                        Select all
                      </Button>

                      <Button
                        variant="link"
                        className="h-auto p-0 text-xs"
                        type="button"
                        onClick={handleUnselectAllRegions}
                        disabled={disabled || selectedRegions.length === 0}
                      >
                        Unselect all
                      </Button>
                    </div>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {!activeCountry ? (
                    <div className="text-textSubtle px-4 py-3 text-sm">
                      Select a country to see regions.
                    </div>
                  ) : filteredRegions.length === 0 ? (
                    <div className="text-textSubtle px-4 py-3 text-sm">
                      No regions match your search.
                    </div>
                  ) : (
                    filteredRegions.map((region) => {
                      const isSelected = selectedValuesSet.has(region.value);
                      const isDisabled =
                        disabledValuesSet.has(region.value) || disabled;
                      return (
                        <label
                          key={region.value}
                          className={clsx(
                            "flex items-center gap-3 px-4 py-2 text-sm",
                            !isDisabled &&
                              "hover:bg-surface-hover cursor-pointer",
                            isDisabled && "text-textSubtle cursor-not-allowed",
                          )}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={isDisabled}
                            onCheckedChange={() => handleToggleRegion(region)}
                          />
                          <span className="text-textDefault">
                            {region.regionName}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
